import { Spin } from "antd";
import { WidgetErrorFallBack } from "components/widgets/WidgetCard";
import React, { Suspense, useMemo } from "react";
import { ErrorBoundary } from "react-error-boundary";

// =============================================================================
// WHY WE USE REACT.LAZY() FOR WIDGET IMPORTS
// =============================================================================
//
// PROBLEM: Circular dependencies in the codebase cause initialization errors
//
// The widget components (e.g., CustomTicketWidgetDetails) import from files like
// CustomDashboardConstants.jsx, which has module-level code that uses constants
// before they're initialized. Example chain:
//
//   AnyaWidgetRenderer (direct import)
//     → CustomTicketWidgetDetails
//       → TicketComboChart
//         → CustomDashboardConstants (TicketComboChartXAxisOptionValue)
//
// At module load time, TicketComboChart.jsx line 173 tries to use
// TicketComboChartXAxisOptionValue in AggMap before it's initialized,
// causing: "Cannot access 'TicketComboChartXAxisOptionValue' before initialization"
//
// SOLUTION: React.lazy() defers module loading until render time
//
// By using React.lazy(), the widget modules are loaded asynchronously AFTER
// all other modules are fully initialized, breaking the circular dependency
// chain. This is a standard React pattern for code splitting that also
// solves initialization order issues.
//
// =============================================================================

// =============================================================================
// WHY WE NEED AnyaWidgetRenderer
// =============================================================================
//
// 1. AVOID CIRCULAR DEPENDENCY:
//    - Cannot use `getCustomWidgetDetailsByWidgetType` from customDashboardUtils.jsx
//    - That file has circular deps: customDashboardUtils ↔ useCustomWidgetFilterFields
//    - AnyaWidgetRenderer does the widget type mapping with lazy loading
//
// 2. CONFIG TRANSFORMATION:
//    - Anya BE sends: { widgetId, widgetType, displayType, filters, config }
//    - Widgets expect: { widgetType, displayType, filterType: "basic", basicFilters }
//
// 3. CONSISTENT STYLING:
//    - Uses widget-card CSS classes for dashboard-consistent appearance
//    - Sets explicit height for proper chart rendering in chat context
//
// =============================================================================

// Lazy-loaded widget components - REQUIRED to avoid circular dependency errors
// DO NOT convert to direct imports - will cause "Cannot access X before initialization"
const CustomWidgetDetails = React.lazy(() => import("../CustomWidgetDetails"));
const CustomTicketWidgetDetails = React.lazy(
  () => import("../CustomTicketWidgetDetails"),
);
const CustomScanWidgetType = React.lazy(
  () => import("../CustomScanWidgetType"),
);
const CustomExceptionWidgetType = React.lazy(
  () => import("../CustomExceptionWidgetType"),
);
const CustomAssetsWidgetType = React.lazy(
  () => import("../CustomAssetsWidgetType"),
);
const CustomAIInventoryWidgetDetails = React.lazy(
  () => import("../CustomWidgets/CustomAIInventoryWidgetDetails"),
);

// Maps Findings displayType → Assets displayType (BE sends Findings format)
// Note: Cannot import from CustomDashboardConstants due to circular dependency
const FINDINGS_TO_ASSETS_DISPLAY_TYPE: Record<string, string> = {
  "Donut Chart": "DONUT_CHART",
  "Bar Chart": "BAR_CHART",
  "Pivot Table": "PIVOT_TABLE",
};

export interface AnyaWidgetConfig {
  displayType?: string;
  widgetType?: string;
  filterType?: string;
  basicFilters?: AnyaWidgetFilters;
  [key: string]: unknown;
}

export interface AnyaWidgetFilters {
  status?: string[];
  severity?: string[];
  product?: number[];
  subProduct?: number[];
  team?: number[];
  [key: string]: unknown;
}

interface AnyaWidgetRendererProps {
  widgetId: string;
  widgetType: string;
  displayType: string;
  config?: AnyaWidgetConfig;
  filters?: AnyaWidgetFilters;
  title?: string;
  description?: string;
  showVisualization: boolean;
}

export default function AnyaWidgetRenderer({
  widgetId,
  widgetType,
  displayType,
  config,
  filters,
  title,
  showVisualization,
}: AnyaWidgetRendererProps) {
  // Merge config with required fields for widget components
  // Transform Anya BE format → Widget expected format
  // Assets expects SCREAMING_SNAKE_CASE except "Asset Coverage" which stays as-is
  const isAssets = widgetType?.toLowerCase() === "assets";
  const mergedConfig = useMemo<AnyaWidgetConfig>(
    () => ({
      ...config,
      widgetType,
      displayType: isAssets
        ? (FINDINGS_TO_ASSETS_DISPLAY_TYPE[displayType] ?? displayType)
        : displayType,
      filterType: "basic", // Use basic filters only
      basicFilters: filters,
    }),
    [config, widgetType, displayType, filters, isAssets],
  );

  // Map widgetType to lazy-loaded component
  // Lazy loading is REQUIRED to break circular dependency chain at module load time
  const WidgetComponent = useMemo(() => {
    const type = widgetType?.toLowerCase();
    switch (type) {
      case "tickets":
        return CustomTicketWidgetDetails;
      case "scan":
        return CustomScanWidgetType;
      case "exceptions":
        return CustomExceptionWidgetType;
      case "assets":
        return CustomAssetsWidgetType;
      case "ai inventory":
        return CustomAIInventoryWidgetDetails;
      case "findings":
      default:
        return CustomWidgetDetails;
    }
  }, [widgetType]);

  if (!showVisualization) {
    return null;
  }

  return (
    <div
      id={`anya-widget-${widgetId}`}
      className="widget-card custom-widget anya-widget-container"
      data-widget-id={widgetId}
    >
      {title && (
        <div className="widget-card-title">
          <div className="text-ellipsis">{title}</div>
        </div>
      )}
      <div className="widget-card-content">
        {/* @ts-expect-error - WidgetErrorFallBack is a JSX component with compatible props */}
        <ErrorBoundary FallbackComponent={WidgetErrorFallBack}>
          <Suspense fallback={<Spin className="anya-widget-spinner" />}>
            {/* @ts-expect-error - Widget components have varying required props */}
            <WidgetComponent
              config={mergedConfig}
              skipGlobalFilters={true}
              configHeight={7}
              configWidth={4}
            />
          </Suspense>
        </ErrorBoundary>
      </div>
    </div>
  );
}
